import { BaseService } from "../BaseService";

export interface ChatConversation {
  id: number;
  name: string;
  agent_id: string;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
  message_count?: number;
  messages?: ChatMessage[];
}

export interface ChatMessage {
  id: number;
  content: string;
  role: "user" | "assistant";
  created_at: string;
  metadata: Record<string, unknown>;
}

export interface CreateConversationRequest {
  name: string;
  agentId: string;
  metadata?: Record<string, unknown>;
}

export interface CreateMessageRequest {
  content: string;
  role: "user" | "assistant";
  metadata?: Record<string, unknown>;
}

export interface UpdateConversationRequest {
  name?: string;
  metadata?: Record<string, unknown>;
}

export class ChatPersistenceService extends BaseService {
  private basePath = "/integrations/ai";
  private lastMessage: {
    conversationId: number;
    content: string;
    role: string;
    timestamp: number;
    response: any;
  } | null = null;

  public async createConversation(
    data: CreateConversationRequest,
  ): Promise<ChatConversation> {
    const requestBody = {
      name: data.name,
      agent_id: data.agentId,
      metadata: data.metadata,
    };

    console.log(
      "ChatPersistenceService.createConversation called with:",
      requestBody,
    );

    const response = await this.postRequest(
      `${this.basePath}/conversations`,
      requestBody,
      undefined,
      true,
      true,
    );
    return response as ChatConversation;
  }

  public async getConversations(agentId?: string): Promise<ChatConversation[]> {
    let url = `${this.basePath}/conversations`;
    if (agentId) {
      url += `?agentId=${encodeURIComponent(agentId)}`;
    }
    const response = await this.getRequest(url);
    return response as ChatConversation[];
  }

  public async getConversation(
    conversationId: number,
  ): Promise<ChatConversation> {
    // The endpoint returns { conversation, messages } as a combined response
    const response = await this.getRequest<{
      conversation: ChatConversation;
      messages: ChatMessage[];
    }>(`${this.basePath}/conversations/${conversationId}`);

    const result = response as any;
    // Handle both formats: { conversation, messages } or flat conversation object
    if (result.conversation) {
      const conversation = result.conversation as ChatConversation;
      conversation.messages = (result.messages as ChatMessage[]) ?? [];
      return conversation;
    }

    // Fallback: treat as flat conversation, fetch messages separately
    const conversation = result as ChatConversation;
    try {
      const messages = await this.getRequest<ChatMessage[]>(
        `${this.basePath}/conversations/${conversationId}/messages`,
      );
      conversation.messages = messages;
    } catch {
      conversation.messages = [];
    }
    return conversation;
  }

  public async createMessage(
    conversationId: number,
    data: CreateMessageRequest,
  ): Promise<ChatMessage> {
    const now = Date.now();
    if (
      this.lastMessage &&
      this.lastMessage.conversationId === conversationId &&
      this.lastMessage.content === data.content &&
      this.lastMessage.role === data.role &&
      now - this.lastMessage.timestamp < 1000
    ) {
      console.warn("Deduplicating message request (sent less than 1s ago)");
      return this.lastMessage.response as ChatMessage;
    }

    const response = await this.postRequest(
      `${this.basePath}/conversations/${conversationId}/messages`,
      {
        content: data.content,
        role: data.role,
        metadata: data.metadata,
      },
      undefined,
      true,
      true,
    );

    this.lastMessage = {
      conversationId,
      content: data.content,
      role: data.role,
      timestamp: now,
      response,
    };

    return response as ChatMessage;
  }

  public async updateConversation(
    conversationId: number,
    data: UpdateConversationRequest,
  ): Promise<ChatConversation> {
    const response = await this.putRequest(
      `${this.basePath}/conversations/${conversationId}`,
      {
        name: data.name,
        metadata: data.metadata,
      },
    );
    return response as ChatConversation;
  }

  public async deleteConversation(conversationId: number): Promise<void> {
    await this.deleteRequest(
      `${this.basePath}/conversations/${conversationId}`,
    );
  }
}

export const chatPersistenceService = new ChatPersistenceService();
